import { NonFieldError } from "@/components/common/SCAlert";
import { useMyPageAPI } from "@/components/examinee/mypage/MyPageAPI";
import {
  getExamineeStatusIndex,
  MyPageMeasurement,
  MyPageTodo,
  MyPageTodoItem,
} from "@/components/examinee/participation/Types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

// ---------------------------------------------------------------------------
// やることリストの定義
// ---------------------------------------------------------------------------

type TodoKey = keyof MyPageTodo;

type TodoItemDef = {
  key: TodoKey;
  label: string;
  getPath: (measurement: MyPageMeasurement) => string;
  /** クリック可能（着手してよい）かどうか */
  isEnabled: (measurement: MyPageMeasurement, item: MyPageTodoItem) => boolean;
};

const TODO_ITEM_DEFS: TodoItemDef[] = [
  {
    key: "primary_interview",
    label: "事前問診に回答",
    getPath: (m) => `/examinee/primary_interview_entrance/${m.id}`,
    // 登録直後（事前問診回答中）から、デバイス発送と並行していつでも回答できる
    isEnabled: (m, item) =>
      !item.completed &&
      getExamineeStatusIndex(m.status) >=
        getExamineeStatusIndex("primary_interview"),
  },
  {
    key: "device_id_registration",
    label: "デバイスIDを登録",
    getPath: (m) => `/examinee/device_id_scan_introduction/${m.id}`,
    isEnabled: (m, item) =>
      !item.completed &&
      getExamineeStatusIndex(m.status) >=
        getExamineeStatusIndex("device_arrival_waiting"),
  },
  {
    key: "measurement_period",
    label: "測定期間を登録",
    getPath: (m) => `/examinee/measurement_period/${m.id}`,
    isEnabled: (m, item) =>
      !item.completed &&
      getExamineeStatusIndex(m.status) >=
        getExamineeStatusIndex("measurement_period_setting"),
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMeasurementPeriod(measurement: MyPageMeasurement): string {
  if (!measurement.measurement_start_date) {
    return "（決定前）";
  }
  const start = format(
    parseISO(measurement.measurement_start_date),
    "yyyy/MM/dd"
  );
  const end = measurement.measurement_end_date
    ? format(parseISO(measurement.measurement_end_date), "yyyy/MM/dd")
    : "";
  return end ? `${start} 〜 ${end}` : start;
}

// ---------------------------------------------------------------------------
// MyPage (main)
// ---------------------------------------------------------------------------

/**
 * 受診者マイページ。「参加中の測定」と「過去の測定」を表示する。
 */
export const MyPage = () => {
  const api = useMyPageAPI();
  const [measurements, setMeasurements] = useState<MyPageMeasurement[] | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(() => {
    api
      .getMeasurements()
      .then((res) => setMeasurements(res.data.results))
      .catch(() => {
        setErrorMessage(
          "測定情報の取得に失敗しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
        );
      });
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  if (errorMessage) {
    return (
      <Stack spacing={6} sx={{ mt: 12, mx: 4, mb: 16 }}>
        <NonFieldError>{errorMessage}</NonFieldError>
      </Stack>
    );
  }

  if (measurements == null) {
    return (
      <Stack alignItems="center" sx={{ py: 20 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const participating = measurements.filter(
    (m) => m.category === "participating"
  );
  const past = measurements.filter((m) => m.category === "past");

  return (
    <Stack spacing={8} sx={{ mt: 8, mb: 16, mx: 4 }}>
      <Typography variant="h6" align="center">
        マイページ
      </Typography>

      <Stack spacing={4}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          参加中の測定
        </Typography>
        {participating.length > 0 ? (
          participating.map((m) => (
            <ParticipatingMeasurementCard key={m.id} measurement={m} />
          ))
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            現在参加中の測定はありません。
          </Typography>
        )}
      </Stack>

      {past.length > 0 && (
        <Stack spacing={4}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            過去の測定
          </Typography>
          {past.map((m) => (
            <PastMeasurementCard key={m.id} measurement={m} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// ParticipatingMeasurementCard
// ---------------------------------------------------------------------------

function ParticipatingMeasurementCard({
  measurement,
}: {
  measurement: MyPageMeasurement;
}) {
  const todo = measurement.todo;
  const todoItems = todo
    ? TODO_ITEM_DEFS.filter((def) => {
        const item = todo[def.key];
        return item != null && item.required;
      })
    : [];

  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        p: 4,
        gap: 3,
      }}
    >
      <Stack direction="row" alignItems="center">
        <Chip
          label={measurement.status_label}
          color="primary"
          variant="outlined"
          size="small"
        />
      </Stack>
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        測定期間：{formatMeasurementPeriod(measurement)}
      </Typography>
      {todoItems.length > 0 && todo && (
        <>
          <Divider />
          <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
            やることリスト
          </Typography>
          <Stack>
            {todoItems.map((def) => {
              const item = todo[def.key] as MyPageTodoItem;
              return (
                <TodoRow
                  key={def.key}
                  label={def.label}
                  completed={item.completed}
                  enabled={def.isEnabled(measurement, item)}
                  to={def.getPath(measurement)}
                />
              );
            })}
          </Stack>
        </>
      )}
    </Stack>
  );
}

function TodoRow({
  label,
  completed,
  enabled,
  to,
}: {
  label: string;
  completed: boolean;
  enabled: boolean;
  to: string;
}) {
  const icon = completed ? (
    <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />
  ) : (
    <RadioButtonUncheckedIcon
      fontSize="small"
      sx={{ color: enabled ? "primary.main" : "text.disabled" }}
    />
  );

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        {icon}
        <Typography
          variant="body2"
          sx={{
            color: enabled ? "text.primary" : "text.disabled",
          }}
        >
          {label}
        </Typography>
      </Stack>
      {enabled && (
        <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
      )}
    </Stack>
  );

  if (!enabled) {
    return content;
  }
  return (
    <RouterLink to={to} style={{ textDecoration: "none" }}>
      {content}
    </RouterLink>
  );
}

// ---------------------------------------------------------------------------
// PastMeasurementCard
// ---------------------------------------------------------------------------

function PastMeasurementCard({
  measurement,
}: {
  measurement: MyPageMeasurement;
}) {
  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        p: 4,
        gap: 3,
      }}
    >
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        測定期間：{formatMeasurementPeriod(measurement)}
      </Typography>
      <Button
        component={RouterLink}
        to={`/examinee/report/${measurement.id}`}
        variant="outlined"
        size="large"
        sx={{ width: "100%" }}
      >
        詳細を見る
      </Button>
    </Stack>
  );
}
